import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import TokenExFrame, { TokenExFramePresentationProps } from "@insite/mobius/TokenExFrame";
import themeColors, { themeFontSizes, themeFontWeights } from "@MasterBluePrint/Styles/variables";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Icon from "@insite/mobius/Icon";
import InfoNewIcon from "@MasterBluePrint/Icons/InfoNew";
import React, { useState } from "react";
import styled, { css } from "styled-components";

interface OwnProps {
    /** @deprecated Specify the "TokenEx" value for the `iframe` property instead. */
    useTokenExGateway?: boolean;
    iframe?: "TokenEx" | "Paymetric" | "Square";
    isTokenExIframeLoaded?: boolean;
    securityCode: string;
    onSecurityCodeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    securityCodeError?: string;
    extendedStyles?: SavedPaymentProfileEntryStyles;
}

export interface SavedPaymentProfileEntryStyles {
    text?: TextFieldPresentationProps;
    tokenExFrameWrapper?: InjectableCss;
    tokenExFrame?: TokenExFramePresentationProps;
    securityCodeHelpModal?: ModalPresentationProps;
    securityCodeHelpModalImageWrapper?: InjectableCss;
    securityCodeHelpImage?: LazyImageProps;
    securityLabel?: TypographyProps;
    shippingAndHandlingValue?: TypographyProps;
    tooltip?: InjectableCss;
    tooltipHeading?: InjectableCss;
    tooltipContent?: InjectableCss;
    tooltipIcon?: InjectableCss;
    securityCodeGridItem?: InjectableCss;
}

export const savedPaymentProfileEntryStyles: SavedPaymentProfileEntryStyles = {
    tokenExFrameWrapper: {
        css: css`
            height: inherit;
        `,
    },
    securityCodeHelpModalImageWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    securityLabel: {
        css: css`
            position: absolute;
            font-size: ${themeFontSizes.size16};
            font-weight: ${themeFontWeights.fontWeightMedium};
            -webkit-letter-spacing: -.019rem;
            -moz-letter-spacing: -.019rem;
            -ms-letter-spacing: -.019rem;
            letter-spacing: -.019rem;
            line-height: 1.438rem;
            left: 9.375rem;
            cursor: pointer;

            @media (max-width: 990px) {
                left: 7rem;
            }
        `,
    },
    tooltipIcon: {
        css: css`
            display: inline-block;
            position: absolute;
            margin-left: 0.3125rem;
            cursor: pointer;
            z-index: 9;

            svg {
                width: 1.5rem;
                height: 1.5rem;
                scale: 0.8;
            }
        `,
    },
    tooltip: {
        css: css`
            background-color: ${themeColors.colorWhite};
            box-shadow: 0 0.4375rem 1.0625rem 0 rgba(0,37,84,0.23);
            position: absolute;
            width: 15.625rem;
            padding: 1.5625rem;
            right: auto;
            left: -5.625rem;
            bottom: 1rem;
            display: none;
            z-index: 2;

            &.active {
                display: block;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            right:-6.625rem;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    tooltipHeading: {
        css: css`
            position: relative;
            color: ${themeColors.colorPrimary};
            font-family: 'Roboto', sans-serif;
            font-size: 1.125rem;
            font-weight: ${themeFontWeights.fontWeightMedium};
            letter-spacing: 0;
            line-height: 1.9375rem;
            margin-bottom: 1.25rem;
            text-align: left;
            &:after {
                height: 0.1875rem;
                width: 0.665rem;
                background-color: ${themeColors.colorDanger};
                content: '';
                display: inline-block;
                position: absolute;
                bottom: -0.125rem;
                left: 0.0625rem;
            }
        `,
    },
    tooltipContent: {
        css: css`
            color: ${themeColors.colorMeasureText};
            font-family: 'Lato',sans-serif;
            font-size: .9375rem;
            letter-spacing: 0.0106rem;
            line-height: 1.5rem;
            text-align: left !important;

            &:after {
                content: '';
                display: block;
                height: 0;
                position: absolute;
                width: 0;
                border: 0.75rem solid;
                border-color: ${themeColors.colorWhite} transparent transparent transparent;
                z-index: 99;
                bottom: -1.4375rem;
                right: 13.250625rem;
            }
        `,
    },
    securityCodeGridItem: { 
        css: css`
            margin-top: 1.875rem;
            
            label {
                > span {
                    margin-left: 1.875rem;
                }
            }
            
        `,
    },
};

const TokenExFrameWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;
const SecurityCodeImageWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;

const SavedPaymentProfileEntry = ({
    useTokenExGateway,
    iframe,
    isTokenExIframeLoaded,
    securityCode,
    onSecurityCodeChange,
    securityCodeError,
    extendedStyles,
}: OwnProps) => {
    const [styles] = useState(() => mergeToNew(savedPaymentProfileEntryStyles, extendedStyles));
    const [isSecurityCodeModalOpen, setIsSecurityCodeModalOpen] = useState(false);

    if (iframe === "Paymetric") {
        return null;
    }

    const handleSecurityCodeHelpLinkClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        setIsSecurityCodeModalOpen(true);
    };

    const handleSecurityCodeModalClose = (_: React.SyntheticEvent<Element, Event>) => setIsSecurityCodeModalOpen(false);

    const securityCodeHelpLabel = translate("Locate my card's security code");

    const resolvedShouldUseTokenEx = iframe === "TokenEx" || useTokenExGateway;

    return (
        <Typography {...styles.securityCodeGridItem} as="div">
            {resolvedShouldUseTokenEx ? (
                <>
                    <TokenExFrame
                        {...styles.tokenExFrame}
                        label={translate("Security code")}
                        tokenExIFrameContainer={
                            <TokenExFrameWrapper {...styles.tokenExFrameWrapper} id="ppTokenExSecurityCode" />
                        }
                        disabled={!isTokenExIframeLoaded}
                        required
                        error={securityCodeError}
                        data-test-selector="checkoutReviewAndSubmit_securityCode"
                    />
                    <Typography {...styles.securityLabel} className="securitylabel">
                        <Typography as="span" {...styles.tooltipIcon}
                            onClick={handleSecurityCodeHelpLinkClick}
                            tabIndex={0}
                            className="security-link"
                        >
                            <Icon src={InfoNewIcon} />
                        </Typography>
                    </Typography>
                </>
            ) : (
                <>
                    <TextField
                        {...styles.text}
                        label={translate("Security code")}
                        value={securityCode}
                        onChange={onSecurityCodeChange}
                        required
                        minLength={3}
                        maxLength={4}
                        error={securityCodeError}
                        data-test-selector="checkoutReviewAndSubmit_securityCode"
                    />
                    <Typography {...styles.securityLabel} className="securitylabel">
                        <Typography as="span" {...styles.tooltipIcon}
                            onClick={handleSecurityCodeHelpLinkClick}
                            tabIndex={0}
                            className="security-link"
                        >
                            <Icon src={InfoNewIcon} />
                        </Typography>
                    </Typography>
                </>
            )}
            <Modal
                headline={securityCodeHelpLabel}
                isOpen={isSecurityCodeModalOpen}
                handleClose={handleSecurityCodeModalClose}
                {...styles.securityCodeHelpModal}
            >
                <SecurityCodeImageWrapper {...styles.securityCodeHelpModalImageWrapper}>
                    <LazyImage
                        src="/images/security_code_sample.jpg"
                        imgProps={{ width: 660, height: 264 }}
                        altText={translate("Location of security code on card")}
                        {...styles.securityCodeHelpImage}
                    />
                </SecurityCodeImageWrapper>
            </Modal>
        </Typography>
    );
};

export default SavedPaymentProfileEntry;
